import { useCallback } from "react";
import useDisconnectPathPickProducts from "@/features/pathpick/hooks/mutation/useDisconnectPathPickProducts";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickMutationProduct,
  PathPickStoreName,
} from "@/features/pathpick/types";

interface Options {
  // 해제할 스토어 — 생략하면 연결된 모든 마켓에서 내린다.
  storeNames?: PathPickStoreName[];
  onSuccess?: () => void;
}

// 연동 해제 / 상품 삭제 실행 — 상품별 처리 내역·신청 상품 내역 모달의 벌크/단건 4개 호출부가 공유한다.
// 서버가 상품별 성공 여부(results)를 내려주므로 부분 실패 안내를 여기서 한 번만 만든다.
const usePathPickUnsyncProducts = () => {
  const { t } = useI18nHelper();
  const { info, warning, add } = useAppToasts({ id: "GLOBAL" });
  const { mutate: disconnectProducts, isPending } =
    useDisconnectPathPickProducts();

  const unsyncProducts = useCallback(
    (
      products: PathPickMutationProduct[],
      deleteFromStore: boolean,
      options?: Options
    ) => {
      if (products.length === 0) {
        return;
      }

      disconnectProducts(
        {
          body: { products, storeNames: options?.storeNames },
          deleteFromStore,
        },
        {
          onSuccess: (data) => {
            const results = data.results ?? [];
            const successCount = results.filter((result) => result.ok).length;
            const failureCount = results.length - successCount;

            if (successCount === 0) {
              // 전부 실패 — useAppToasts.error는 에러 객체용이라 add로 직접 띄운다.
              add({
                status: "error",
                message: t(
                  "path_pick:sync_download_history.unsync_result.failed"
                ),
                id: "GLOBAL",
              });
              return;
            }

            if (failureCount > 0) {
              warning(
                t("path_pick:sync_download_history.unsync_result.partial", {
                  success: successCount,
                  failure: failureCount,
                })
              );
            } else {
              info(
                t(
                  deleteFromStore
                    ? "path_pick:sync_download_history.unsync_result.delete_success"
                    : "path_pick:sync_download_history.unsync_result.unsync_success",
                  { count: successCount }
                )
              );
            }

            // 일부라도 처리됐으면 선택 해제·모달 닫기는 진행한다 (목록 갱신은 mutation 훅이 담당).
            options?.onSuccess?.();
          },
        }
      );
    },
    [add, disconnectProducts, info, t, warning]
  );

  return { unsyncProducts, isPending };
};

export default usePathPickUnsyncProducts;
